<template>
  <div class="homework-diagnosis-system">
    <!-- 导航栏 -->
    <nav class="navbar">
      <div class="navbar-content">
        <div class="logo">
          <span>📚</span>
          <span>智能作业诊断系统</span>
        </div>
        <div class="nav-menu">
          <div 
            class="nav-item" 
            v-for="item in navItems" 
            :key="item.key"
            @click="switchTab(item.key)"
          >
            <span>{{ item.icon }}</span>
            <span>{{ item.label }}</span>
          </div>
        </div>
        <div class="user-info">
          <span>👤</span>
          <span>张老师</span>
          <span>试点账号1/4</span>
        </div>
      </div>
    </nav>

    <!-- 主内容区 -->
    <div class="main-container">
      <!-- 功能选项卡 -->
      <div class="tabs">
        <div class="tab-header">
          <div 
            v-for="(tab, index) in tabs" 
            :key="tab.key"
            class="tab-item" 
            :class="{ active: activeTab === tab.key }"
            @click="switchTab(tab.key)"
          >
            {{ tab.label }}
          </div>
        </div>
      </div>

      <!-- 作业上传区域 -->
      <div v-show="activeTab === 'upload'" class="tab-content">
        <div class="upload-section">
          <h2 style="margin-bottom: 1.5rem;">上传作业文件</h2>
          <div 
            class="upload-area" 
            :class="{ dragging: isDragging }"
            @drop.prevent="handleDrop"
            @dragover.prevent="handleDragOver"
            @dragleave.prevent="handleDragLeave"
            @click="$refs.fileInput.click()"
          >
            <div class="upload-icon">📁</div>
            <h3>拖拽文件到此处或点击上传</h3>
            <p style="color: #9ca3af; margin-top: 0.5rem;">支持批量上传，单个文件不超过50MB</p>
            <div class="supported-formats">
              <span class="format-tag" v-for="format in supportedFormats" :key="format.type">
                {{ format.icon }} {{ format.type }}
              </span>
            </div>
            <input 
              ref="fileInput"
              type="file" 
              multiple 
              accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx" 
              style="display: none;"
              @change="handleFileSelect"
            >
          </div>
          <div style="margin-top: 1rem; display: flex; gap: 1rem;">
            <button class="btn btn-primary" @click="$refs.fileInput.click()">
              选择文件
            </button>
            <button class="btn btn-secondary" @click="startBatchProcess">
              开始批量处理
            </button>
          </div>
        </div>

        <!-- 批改结果展示 -->
        <div class="results-section" v-if="showResults">
          <div class="result-card">
            <div class="result-header">
              <h3>批改结果</h3>
              <div class="score-display">{{ currentScore }}分</div>
            </div>
            <h4>识别错误：</h4>
            <ul class="error-list">
              <li class="error-item" v-for="error in errors" :key="error.id">
                <strong>{{ error.title }}：</strong>{{ error.description }}
              </li>
            </ul>
            <h4 style="margin-top: 1.5rem;">改进建议：</h4>
            <ul class="error-list">
              <li class="suggestion-item" v-for="suggestion in suggestions" :key="suggestion.id">
                {{ suggestion.text }}
              </li>
            </ul>
          </div>

          <div class="pdf-preview">
            <h3 style="margin-bottom: 1rem;">作业批注预览</h3>
            <div class="pdf-toolbar">
              <div style="display: flex; gap: 1rem;">
                <button class="btn btn-secondary" @click="previousPage">⬅️ 上一页</button>
                <span>第 {{ currentPage }} / {{ totalPages }} 页</span>
                <button class="btn btn-secondary" @click="nextPage">➡️ 下一页</button>
              </div>
              <div style="display: flex; gap: 1rem;">
                <button class="btn btn-secondary" @click="zoomIn">🔍 放大</button>
                <button class="btn btn-secondary" @click="zoomOut">🔍 缩小</button>
                <button class="btn btn-primary" @click="downloadAnnotated">💾 下载批注版</button>
              </div>
            </div>
            <div class="pdf-viewer">
              <div style="text-align: center; color: #9ca3af;">
                <div style="font-size: 4rem; margin-bottom: 1rem;">📄</div>
                <p>PDF预览区域</p>
                <p>批注会自动标记在错误位置</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 企业真题库 -->
      <div v-show="activeTab === 'bank'" class="tab-content">
        <div class="question-bank">
          <h2 style="margin-bottom: 1.5rem;">企业真题库（50+ 真实工程问题）</h2>
          <div class="company-filter">
            <div 
              v-for="company in companies" 
              :key="company"
              class="company-tag" 
              :class="{ active: selectedCompany === company }"
              @click="selectCompany(company)"
            >
              {{ company }}
            </div>
          </div>
          <div class="question-list">
            <div 
              v-for="question in filteredQuestions" 
              :key="question.id"
              class="question-card" 
              @click="openQuestion(question)"
            >
              <h4>{{ question.company }} - {{ question.title }}</h4>
              <p style="color: #6b7280; margin-top: 0.5rem;">{{ question.description }}</p>
              <div style="margin-top: 1rem; display: flex; gap: 1rem;">
                <span class="format-tag">难度：{{ question.difficulty }}</span>
                <span class="format-tag">类型：{{ question.type }}</span>
                <span class="format-tag">提交：{{ question.submissions }}次</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 教师审核 -->
      <div v-show="activeTab === 'review'" class="tab-content">
        <div class="review-panel">
          <h2 style="margin-bottom: 1.5rem;">待审核作业（AI评分待确认）</h2>
          <div v-for="item in reviewItems" :key="item.id" class="review-item">
            <div style="display: flex; justify-content: space-between; align-items: center;">
              <div>
                <h4>学生：{{ item.studentName }} - {{ item.subject }}</h4>
                <p style="color: #6b7280; margin-top: 0.5rem;">提交时间：{{ item.submitTime }}</p>
              </div>
              <div style="text-align: center;">
                <p style="color: #6b7280;">AI评分</p>
                <div class="score-display">{{ item.aiScore }}分</div>
              </div>
            </div>
            <div style="margin-top: 1rem; padding: 1rem; background: #f9f9fb; border-radius: 8px;">
              <p><strong>{{ item.aiScore >= 80 ? '优点' : '主要扣分项' }}：</strong></p>
              <ul style="margin-left: 1.5rem; margin-top: 0.5rem;">
                <li v-for="point in item.points" :key="point">{{ point }}</li>
              </ul>
            </div>
            <div class="review-actions">
              <input 
                type="number" 
                v-model="item.modifiedScore"
                placeholder="修改分数" 
                style="padding: 0.5rem; border: 1px solid #e5e7eb; border-radius: 8px; width: 120px;"
              >
              <button class="btn btn-success" @click="confirmScore(item)">✅ 确认评分</button>
              <button class="btn btn-secondary" @click="addComment(item)">📝 添加评语</button>
              <button class="btn btn-danger" @click="regrade(item)">❌ 重新批改</button>
            </div>
          </div>
        </div>
      </div>

      <!-- 数据报告 -->
      <div v-show="activeTab === 'report'" class="tab-content">
        <h2 style="margin-bottom: 1.5rem;">班级作业质量报告</h2>
        <div class="report-section">
          <div class="stat-card" v-for="stat in statistics" :key="stat.label">
            <h4>{{ stat.label }}</h4>
            <div class="stat-value">{{ stat.value }}</div>
            <p :style="{ color: stat.color }">{{ stat.trend }}</p>
          </div>
        </div>
        
        <div class="chart-container">
          <div style="text-align: center;">
            <h3>分数分布图</h3>
            <div style="margin-top: 2rem;">
              <canvas ref="scoreChart" width="600" height="300"></canvas>
            </div>
            <p style="color: #6b7280; margin-top: 1rem;">
              模拟数据：60-70分(4人) | 70-80分(12人) | 80-90分(20人) | 90-100分(14人)
            </p>
          </div>
        </div>

        <div style="margin-top: 2rem; background: white; border-radius: 12px; padding: 2rem; box-shadow: 0 2px 12px rgba(0,0,0,0.08);">
          <h3 style="margin-bottom: 1rem;">常见错误分析</h3>
          <div style="display: grid; gap: 1rem;">
            <div 
              v-for="error in commonErrors" 
              :key="error.id"
              style="padding: 1rem; background: #fef2f2; border-radius: 8px;"
            >
              <strong>{{ error.rank }}. {{ error.type }}（出现{{ error.count }}次）</strong>
              <p style="margin-top: 0.5rem;">{{ error.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 模态框 -->
    <div class="modal" :class="{ show: showModal }">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ modalTitle }}</h3>
          <span class="close-btn" @click="closeModal">×</span>
        </div>
        <div class="modal-body">
          <h4>题目要求：</h4>
          <p>{{ selectedQuestion ? selectedQuestion.fullDescription : '' }}</p>
          <div style="margin-top: 2rem;">
            <button class="btn btn-primary" @click="assignHomework">布置给学生</button>
            <button class="btn btn-secondary" @click="viewSolutions">查看参考答案</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 通知提示 -->
    <transition name="notification">
      <div v-if="notification.show" class="notification">
        {{ notification.message }}
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'HomeworkDiagnosis',
  data() {
    return {
      activeTab: 'upload',
      isDragging: false,
      showResults: false,
      showModal: false,
      modalTitle: '题目详情',
      selectedQuestion: null,
      currentScore: 85,
      currentPage: 1,
      totalPages: 5,
      selectedCompany: '全部企业',
      notification: {
        show: false,
        message: ''
      },
      
      // 导航项
      navItems: [
        { key: 'upload', icon: '📤', label: '作业上传' },
        { key: 'bank', icon: '📋', label: '企业真题' },
        { key: 'review', icon: '✅', label: '教师审核' },
        { key: 'report', icon: '📊', label: '数据报告' }
      ],
      
      // 标签页
      tabs: [
        { key: 'upload', label: '作业批改' },
        { key: 'bank', label: '企业真题库' },
        { key: 'review', label: '人工审核' },
        { key: 'report', label: '班级报告' }
      ],
      
      // 支持的文件格式
      supportedFormats: [
        { icon: '📷', type: 'JPG/PNG' },
        { icon: '📄', type: 'PDF' },
        { icon: '📝', type: 'WORD' },
        { icon: '📊', type: 'EXCEL' }
      ],
      
      // 错误列表
      errors: [
        { id: 1, title: '第2题', description: '语法错误 - "Their is" 应改为 "There is"' },
        { id: 2, title: '第5题', description: '计算错误 - 结果应为 156 而非 165' }
      ],
      
      // 建议列表
      suggestions: [
        { id: 1, text: '建议加强 there/their/they\'re 的区别练习' },
        { id: 2, text: '注意进位运算的准确性' }
      ],
      
      // 企业列表
      companies: ['全部企业', '🏢 阿里巴巴', '🏢 腾讯', '🏢 字节跳动', '🏢 华为', '🏢 百度', '🏢 美团'],
      
      // 真题列表
      questions: [
        {
          id: 1,
          company: '🏢 阿里巴巴',
          title: '分布式系统设计',
          description: '设计一个支持千万级并发的电商秒杀系统，要求考虑缓存、限流、降级等策略...',
          difficulty: '⭐⭐⭐⭐',
          type: '系统设计',
          submissions: 234,
          fullDescription: '设计一个支持千万级并发的电商秒杀系统，要求考虑缓存、限流、降级等策略。需要详细说明系统架构、技术选型、数据流转等关键要素。'
        },
        {
          id: 2,
          company: '🏢 腾讯',
          title: '算法优化',
          description: '给定一个社交网络图，设计算法找出影响力最大的K个用户节点...',
          difficulty: '⭐⭐⭐⭐⭐',
          type: '算法',
          submissions: 189,
          fullDescription: '给定一个社交网络图，设计算法找出影响力最大的K个用户节点。需要考虑时间复杂度和空间复杂度的优化。'
        },
        {
          id: 3,
          company: '🏢 字节跳动',
          title: '前端工程化',
          description: '实现一个微前端框架，支持多个子应用的动态加载和通信...',
          difficulty: '⭐⭐⭐',
          type: '前端',
          submissions: 156,
          fullDescription: '实现一个微前端框架，支持多个子应用的动态加载和通信。需要考虑沙箱隔离、样式隔离、通信机制等核心功能。'
        }
      ],
      
      // 待审核项目
      reviewItems: [
        {
          id: 1,
          studentName: '李明',
          subject: '数据结构作业',
          submitTime: '2024-01-15 14:30',
          aiScore: 78,
          modifiedScore: null,
          points: [
            '二叉树遍历算法实现有误（-10分）',
            '时间复杂度分析不准确（-7分）',
            '代码注释不充分（-5分）'
          ]
        },
        {
          id: 2,
          studentName: '王芳',
          subject: '英语写作',
          submitTime: '2024-01-15 15:45',
          aiScore: 92,
          modifiedScore: null,
          points: [
            '主题明确，论述充分（+）',
            '词汇使用丰富准确（+）',
            '个别语法小错误（-3分）'
          ]
        }
      ],
      
      // 统计数据
      statistics: [
        { label: '平均分', value: '82.5', trend: '↑ 3.2% vs 上周', color: '#10b981' },
        { label: '提交率', value: '96%', trend: '48/50 已提交', color: '#6b7280' },
        { label: '优秀率', value: '28%', trend: '14名学生90分以上', color: '#667eea' },
        { label: '待改进', value: '8%', trend: '4名学生需要辅导', color: '#ef4444' }
      ],
      
      // 常见错误
      commonErrors: [
        { id: 1, rank: 1, type: '英语语法错误', count: 32, description: '主要集中在时态使用和主谓一致方面' },
        { id: 2, rank: 2, type: '数学计算错误', count: 28, description: '进位运算和小数点处理需要加强' },
        { id: 3, rank: 3, type: '程序逻辑错误', count: 15, description: '循环边界条件和递归终止条件理解不足' }
      ]
    }
  },
  
  computed: {
    filteredQuestions() {
      if (this.selectedCompany === '全部企业') {
        return this.questions
      }
      return this.questions.filter(q => q.company === this.selectedCompany)
    }
  },
  
  mounted() {
    this.drawChart()
  },
  
  methods: {
    // 切换标签页
    switchTab(tabName) {
      this.activeTab = tabName
      if (tabName === 'report') {
        this.$nextTick(() => {
          this.drawChart()
        })
      }
    },
    
    // 文件处理
    handleDragOver() {
      this.isDragging = true
    },
    
    handleDragLeave() {
      this.isDragging = false
    },
    
    handleDrop(e) {
      this.isDragging = false
      const files = e.dataTransfer.files
      this.handleFiles(files)
    },
    
    handleFileSelect(e) {
      const files = e.target.files
      this.handleFiles(files)
    },
    
    handleFiles(files) {
      console.log('上传文件数：', files.length)
      for (let file of files) {
        console.log('文件名：', file.name, '大小：', (file.size / 1024 / 1024).toFixed(2) + 'MB')
      }
      this.showNotification('文件上传成功！开始处理...')
      this.showResults = true
    },
    
    // 批量处理
    startBatchProcess() {
      this.showNotification('开始批量处理作业...')
      setTimeout(() => {
        this.showNotification('批量处理完成！')
        this.showResults = true
      }, 2000)
    },
    
    // PDF操作
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--
      }
    },
    
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
      }
    },
    
    zoomIn() {
      this.showNotification('放大预览')
    },
    
    zoomOut() {
      this.showNotification('缩小预览')
    },
    
    downloadAnnotated() {
      this.showNotification('开始下载批注版PDF...')
    },
    
    // 企业真题
    selectCompany(company) {
      this.selectedCompany = company
    },
    
    openQuestion(question) {
      this.selectedQuestion = question
      this.modalTitle = `${question.company} - ${question.title}`
      this.showModal = true
    },
    
    closeModal() {
      this.showModal = false
      this.selectedQuestion = null
    },
    
    assignHomework() {
      this.showNotification('题目已分配给学生！')
      this.closeModal()
    },
    
    viewSolutions() {
      this.showNotification('正在加载参考答案...')
    },
    
    // 教师审核
    confirmScore(item) {
      const score = item.modifiedScore || item.aiScore
      this.showNotification(`已确认评分：${score}分`)
    },
    
    addComment(item) {
      this.showNotification('请在弹出窗口中添加评语')
    },
    
    regrade(item) {
      this.showNotification('正在重新批改...')
    },
    
    // 显示通知
    showNotification(message) {
      this.notification.message = message
      this.notification.show = true
      setTimeout(() => {
        this.notification.show = false
      }, 3000)
    },
    
    // 绘制图表
    drawChart() {
      const canvas = this.$refs.scoreChart
      if (canvas && canvas.getContext) {
        const ctx = canvas.getContext('2d')
        
        // 清空画布
        ctx.clearRect(0, 0, 600, 300)
        
        // 模拟数据
        const data = [4, 12, 20, 14]
        const labels = ['60-70', '70-80', '80-90', '90-100']
        const colors = ['#ef4444', '#f59e0b', '#10b981', '#667eea']
        
        // 绘制柱状图
        const barWidth = 80
        const spacing = 40
        const maxHeight = 200
        const maxValue = Math.max(...data)
        
        data.forEach((value, index) => {
          const height = (value / maxValue) * maxHeight
          const x = 50 + index * (barWidth + spacing)
          const y = 250 - height
          
          // 绘制柱子
          ctx.fillStyle = colors[index]
          ctx.fillRect(x, y, barWidth, height)
          
          // 绘制数值
          ctx.fillStyle = '#333'
          ctx.font = '16px Arial'
          ctx.textAlign = 'center'
          ctx.fillText(value + '人', x + barWidth/2, y - 10)
          
          // 绘制标签
          ctx.fillText(labels[index], x + barWidth/2, 270)
        })
      }
    }
  }
}
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.homework-diagnosis-system {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', sans-serif;
  background: #f5f7fa;
  color: #333;
  min-height: 100vh;
}

/* 顶部导航栏 */
.navbar {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 1rem 2rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  position: sticky;
  top: 0;
  z-index: 1000;
}

.navbar-content {
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logo {
  font-size: 1.5rem;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.nav-menu {
  display: flex;
  gap: 2rem;
  align-items: center;
}

.nav-item {
  cursor: pointer;
  transition: opacity 0.3s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.nav-item:hover {
  opacity: 0.8;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: rgba(255,255,255,0.2);
  padding: 0.5rem 1rem;
  border-radius: 25px;
}

/* 主要内容区域 */
.main-container {
  max-width: 1400px;
  margin: 2rem auto;
  padding: 0 2rem;
}

/* 功能选项卡 */
.tabs {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
  margin-bottom: 2rem;
  overflow: hidden;
}

.tab-header {
  display: flex;
  border-bottom: 2px solid #f0f2f5;
}

.tab-item {
  flex: 1;
  padding: 1.2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  font-weight: 500;
  position: relative;
}

.tab-item:hover {
  background: #f9f9fb;
}

.tab-item.active {
  color: #667eea;
  background: #f9f9fb;
}

.tab-item.active::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  right: 0;
  height: 2px;
  background: #667eea;
}

/* 上传区域 */
.upload-section {
  background: white;
  border-radius: 12px;
  padding: 3rem;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
  margin-bottom: 2rem;
}

.upload-area {
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  padding: 4rem 2rem;
  text-align: center;
  transition: all 0.3s;
  cursor: pointer;
  background: #fafbfc;
}

.upload-area:hover {
  border-color: #667eea;
  background: #f9f9fb;
}

.upload-area.dragging {
  border-color: #667eea;
  background: #f0f3ff;
}

.upload-icon {
  font-size: 3rem;
  color: #9ca3af;
  margin-bottom: 1rem;
}

.supported-formats {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 1rem;
  flex-wrap: wrap;
}

.format-tag {
  background: #f3f4f6;
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: 0.85rem;
  color: #6b7280;
}

/* 批改结果展示 */
.results-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-top: 2rem;
}

.result-card {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
}

.result-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.score-display {
  font-size: 2.5rem;
  font-weight: bold;
  color: #667eea;
}

.error-list {
  list-style: none;
  margin-top: 1rem;
}

.error-item {
  background: #fef2f2;
  border-left: 4px solid #ef4444;
  padding: 1rem;
  margin-bottom: 0.5rem;
  border-radius: 4px;
}

.suggestion-item {
  background: #f0fdf4;
  border-left: 4px solid #10b981;
  padding: 1rem;
  margin-bottom: 0.5rem;
  border-radius: 4px;
}

/* PDF预览区域 */
.pdf-preview {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
  height: 600px;
  display: flex;
  flex-direction: column;
}

.pdf-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #f9f9fb;
  border-radius: 8px;
  margin-bottom: 1rem;
}

.pdf-viewer {
  flex: 1;
  background: #f5f5f5;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: auto;
}

/* 真题库 */
.question-bank {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
}

.company-filter {
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
}

.company-tag {
  padding: 0.5rem 1rem;
  border-radius: 25px;
  background: #f3f4f6;
  cursor: pointer;
  transition: all 0.3s;
}

.company-tag:hover {
  background: #e5e7eb;
}

.company-tag.active {
  background: #667eea;
  color: white;
}

.question-list {
  display: grid;
  gap: 1rem;
}

.question-card {
  background: #f9f9fb;
  padding: 1.5rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  border: 1px solid transparent;
}

.question-card:hover {
  border-color: #667eea;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.1);
}

/* 数据报告 */
.report-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
  text-align: center;
}

.stat-value {
  font-size: 3rem;
  font-weight: bold;
  color: #667eea;
  margin: 1rem 0;
}

.chart-container {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
  height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 教师确认界面 */
.review-panel {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
}

.review-item {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1rem;
}

.review-actions {
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
}

/* 按钮样式 */
.btn {
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover {
  background: #5a67d8;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.btn-secondary {
  background: #f3f4f6;
  color: #4b5563;
}

.btn-secondary:hover {
  background: #e5e7eb;
}

.btn-success {
  background: #10b981;
  color: white;
}

.btn-success:hover {
  background: #059669;
}

.btn-danger {
  background: #ef4444;
  color: white;
}

.btn-danger:hover {
  background: #dc2626;
}

/* 模态框 */
.modal {
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.5);
  z-index: 2000;
  align-items: center;
  justify-content: center;
}

.modal.show {
  display: flex;
}

.modal-content {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.modal-body h4 {
  margin-bottom: 1rem;
}

.close-btn {
  font-size: 1.5rem;
  cursor: pointer;
  color: #9ca3af;
}

.close-btn:hover {
  color: #4b5563;
}

/* 通知提示 */
.notification {
  position: fixed;
  top: 20px;
  right: 20px;
  background: #667eea;
  color: white;
  padding: 1rem 1.5rem;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  z-index: 3000;
}

/* 过渡动画 */
.notification-enter-active, .notification-leave-active {
  transition: all 0.3s ease;
}

.notification-enter, .notification-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .nav-menu {
    display: none;
  }
  
  .results-section {
    grid-template-columns: 1fr;
  }
  
  .report-section {
    grid-template-columns: 1fr;
  }
  
  .tab-header {
    overflow-x: auto;
  }
  
  .tab-item {
    white-space: nowrap;
  }
  
  .main-container {
    padding: 0 1rem;
  }
  
  .upload-section {
    padding: 2rem 1rem;
  }
}
</style>